import { useEffect, useState } from 'react';
import { message } from 'antd';
import { gql } from 'graphql-tag';
import { useApolloClient } from '@apollo/client';
import {
	REVERSE_MAPING_BADGE_TYPE,
	ICON_COMPLETE_MAP,
	BADGE_TYPE
} from '../../RnRCommonComponent/CoreBadge/rewardsConstants.js';

const QUERY_MY_BADGE_ASSIGNMENTS = gql`
	query QueryMyBadgeAssignments($companyId: ID!, $userId: ID!, $approvalStatus: ApprovalStatus!, $limit: Int) {
		queryBadgeAssignments(
			companyId: $companyId
			userId: $userId
			approvalStatus: $approvalStatus
			limit: $limit
		) {
			items {
				id
				badgeId
				userId
				awardedBy
				approvalStatus
				rewardBonus
				points
				createdAt
				updatedAt
				completedAt
				badge {
					id
					name
					label
					description
					image
					color
					gradientColor
					points
					bonusPoints
					rewardBonus
					iconId
					companyId
					icon {
						id
						name
						icon
						iconType
						code
					}
					badgeTypeId
					badgeType {
						id
						name
						code
						shape {
							id
							name
							shape
						}
					}
					snappyGrantId
					snappyGrant {
						id
						name
						costInCurrency
						externalSnappyId
						snappyCampaignTierId
					}
				}
				user {
					id
					firstName
					lastName
					emailAddress
				}
			}
			nextToken
		}
	}
`;

const useMyBadgeAssignments = (currentUser, shouldRefresh, setShouldRefresh) => {
	const [isLoading, setIsLoading] = useState(false);
	const [badgeList, setBadgeList] = useState([]);
	const [hasMore, setHasMore] = useState(false);
	const apolloClient = useApolloClient();

	const fetchMyBadgeAssignments = async () => {
		if (!currentUser?.id || !currentUser?.company?.id) {
			return;
		}

		setIsLoading(true);
		try {
			const { data, error } = await apolloClient.query({
				query: QUERY_MY_BADGE_ASSIGNMENTS,
				variables: {
					companyId: currentUser.company.id,
					userId: currentUser.id,
					approvalStatus: 'Approved',
					limit: 100
				},
				fetchPolicy: 'network-only'
			});

			if (error) {
				console.error('Error fetching my badge assignments:', error);
				message.error('Failed to fetch your recognitions! Try again later.');
				return;
			}

			if (data?.queryBadgeAssignments?.items) {
				// Filter to only include badges from the current user's company and transform
				const transformedBadges = data.queryBadgeAssignments.items
					.filter(assignment => assignment.badge?.companyId === currentUser.company.id || !assignment.badge?.companyId)
					.map(assignment => {
					const badge = assignment.badge;
					
					// Map badge type from ID to code like in the original hook
					const badgeTypeCode = REVERSE_MAPING_BADGE_TYPE[badge?.badgeTypeId] || '';
					const badgeType = BADGE_TYPE[badgeTypeCode];
					
					// Get icon info from the fetched icon data or fallback to icon map
					let iconInfo = null;
					if (badge?.icon) {
						iconInfo = badge.icon;
					} else if (badge?.iconId) {
						iconInfo = ICON_COMPLETE_MAP.find(icon => icon.id === badge.iconId);
					}
					
					return {
						// Badge assignment fields
						assignmentId: assignment.id,
						badgeId: assignment.badgeId,
						userId: assignment.userId,
						awardedBy: assignment.awardedBy,
						approvalStatus: assignment.approvalStatus,
						rewardBonus: assignment.rewardBonus,
						points: assignment.points || badge?.points,
						createdAt: assignment.createdAt,
						updatedAt: assignment.updatedAt,
						completedAt: assignment.completedAt,
						
						// Badge fields for display
						id: badge?.id,
						name: badge?.name,
						label: badge?.label,
						description: badge?.description,
						image: badge?.image,
						color: badge?.color,
						gradientColor: badge?.gradientColor,
						bonusPoints: badge?.bonusPoints,
						iconId: badge?.iconId,
						badgeTypeId: badge?.badgeTypeId,
						snappyGrantId: badge?.snappyGrantId,
						snappyGrant: badge?.snappyGrant,
						
						// Essential fields for proper badge rendering
						badgeType: badgeType,
						iconInfo: iconInfo,
						
						// Additional fields for compatibility
						issuedDate: assignment.completedAt || assignment.updatedAt,
						issued: 1, // For display purposes - shows "1 Issued" under badge
						recipient: assignment.user,
						
						// For clicking on badges
						badge: {
							...badge,
							completedAt: assignment.completedAt,
							issuedDate: assignment.completedAt || assignment.updatedAt,
							badgeType: badgeType,
							iconInfo: iconInfo
						},
						rawAssignment: assignment
					};
				});

				setBadgeList(transformedBadges);
				setShouldRefresh && setShouldRefresh(false);
				
				// Cache the results
				localStorage.setItem('myBadgeAssignments', JSON.stringify(transformedBadges));
			}
		} catch (error) {
			console.error('Exception when fetching my badge assignments:', error);
			message.error('Failed to fetch your recognitions! Try again later.');
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (shouldRefresh) {
			fetchMyBadgeAssignments();
		}
	}, [shouldRefresh]);

	useEffect(() => {
		fetchMyBadgeAssignments();
	}, [currentUser?.id, currentUser?.company?.id]);

	const fetchMoreData = () => {
		// For now, we'll just fetch all at once since we're using limit: 100
		// In the future, this could implement pagination with nextToken
	};

	// Try to get cached data if available
	const cachedBadgeList = JSON.parse(localStorage.getItem('myBadgeAssignments') || '[]');

	return { 
		isLoading, 
		badgeList: badgeList.length > 0 ? badgeList : cachedBadgeList, 
		hasMore, 
		fetchMoreData 
	};
};

export default useMyBadgeAssignments;